/**
 * 句子状态配色 + 挖空切分
 * ============================================================
 * 对齐 Midoo 的做法（截图观察得出）：
 *   · 蓝色 = **需要你补出来的部分**（待产出）
 *   · 绿色 = **完整 / 答对了**
 * 目的是让用户一眼看出「现在该我说什么」和「这句过了」，
 * 不用去读按钮文案猜当前状态。
 *
 * ── 与 COLOR_CLASS 的分工（重要，别混用）──────────────────────
 *   本文件（句子级）：**作答前后**的句子状态色 —— 绿 / 蓝
 *   COLOR_CLASS（字级）：**作答后**的逐字准确度色 —— 蓝 / 黄 / 红
 *
 * 两套都用到蓝色，但出现的位置与时机不同，可以区分：
 *   · 蓝色挖空只出现在**作答前**的题目区，形态是连续下划线「＿＿」
 *   · 字级蓝色出现在**作答后**的「你说的」区块，且该区块自带图例
 * 之所以没有把挖空改成别的颜色：Midoo 用蓝表示"待补全"已成用户直觉，
 * 换成橙/紫反而要重新教育用户。
 * 改动这里时**务必同步检查 COLOR_CLASS 的图例文案**，避免一个蓝两种含义被混淆。
 */

/**
 * 句子状态 → 文字颜色类
 *
 * pending 用深色而非浅灰：句子是这一屏的主角，浅灰会显得像禁用态。
 */
export const SENTENCE_COLOR = {
  /** 待作答：中性色，聚焦文本本身 */
  pending: 'text-zinc-900 dark:text-zinc-50',
  /** 需要补出来的部分 */
  hint: 'text-blue-600 dark:text-blue-400',
  /** 完整 / 答对 */
  good: 'text-emerald-600 dark:text-emerald-400',
};

/**
 * 挖空占位符
 *
 * 后端 `config/sessions.js` 的 buildFillStep 用全角下划线「＿＿」占位
 * （`clean.replace(word, '＿＿')`）。这里用正则容忍一个或多个。
 */
const BLANK_PATTERN = /＿+/g;

/**
 * 把挖空句切成可分别着色的片段
 *
 * @param {string} prompt - 含「＿＿」的句子
 * @returns {Array<{type:'text'|'blank', value:string}>}
 *
 * @example
 *   splitByBlank('唔該，＿＿。')
 *   // → [{type:'text',value:'唔該，'},{type:'blank',value:'＿＿'},{type:'text',value:'。'}]
 */
export function splitByBlank(prompt) {
  const text = String(prompt ?? '');
  if (!text) return [];

  const segments = [];
  let cursor = 0;
  let match;
  BLANK_PATTERN.lastIndex = 0;

  while ((match = BLANK_PATTERN.exec(text)) !== null) {
    if (match.index > cursor) {
      segments.push({ type: 'text', value: text.slice(cursor, match.index) });
    }
    segments.push({ type: 'blank', value: match[0] });
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    segments.push({ type: 'text', value: text.slice(cursor) });
  }

  // 没有挖空时也要返回一段，调用方无需分支处理
  return segments.length ? segments : [{ type: 'text', value: text }];
}

/**
 * 句子是否含挖空
 * @param {string} prompt
 * @returns {boolean}
 */
export function hasBlank(prompt) {
  return splitByBlank(prompt).some((s) => s.type === 'blank');
}

/**
 * 取句子整体的状态色
 *
 * @param {boolean} passed - 该步是否已过关
 * @returns {string} Tailwind 类
 */
export function sentenceStateColor(passed) {
  return passed ? SENTENCE_COLOR.good : SENTENCE_COLOR.pending;
}
